import { Box, BoxProps } from "@chakra-ui/react";
import React, { PropsWithChildren } from "react";

import DefaultLayout from "./DefaultLayout";

type DefaultPageLayoutProps = PropsWithChildren<BoxProps>;

/**
 * Like `DefaultLayout`, but with some layout assumptions made
 * for `children`
 *  - `children` will be in an area with a min-height to take up the
 *     rest of the viewport after the page header (so that the total
 *     min-height of the page is 100vh)
 *  - A `maxW` (or `maxWidth`) can be applied, above which `children`
 *    will be centered on the page horizontally
 *
 * This layout is similar to:
 * ```
 * <DefaultLayout>
 *   <PageContent>
 *     {children}
 *   </PageContent>
 * </DefaultLayout>
 * ```
 * but `PageContent` comes with a pagePadding that we don't want
 *
 * TODO: Many of today's usages of `<DefaultLayout />` could be replaced
 *       with this component.
 */
const DefaultPageLayout: React.FC<DefaultPageLayoutProps> = ({
  children,
  maxW,
  maxWidth,
  ...styles
}) => {
  return (
    <DefaultLayout>
      {/* 
        On desktop (lg+): flex: 1 and minH: 0 allow this to fill remaining space 
        in the flex parent (SidebarNavBase) and shrink when CallInProgressBanner is shown.
        On mobile: use auto height for natural content flow.
      */}
      <Box
        bg="white"
        display="flex"
        flex={{ base: "none", lg: "1" }}
        minHeight={{ base: "auto", lg: "0" }}
        {...styles}
      >
        <Box maxW={maxW || maxWidth} marginX="auto" flex="1">
          {children}
        </Box>
      </Box>
    </DefaultLayout>
  );
};

export default DefaultPageLayout;
